/**
 * fragmentUtils — utilities for fragment HTML processing and loading.
 *
 * Most exports below (parseFragment, sanitizeFragmentHtml, extractFragment*,
 * injectScript/injectStylesheet, applyFragmentState, scopeStates,
 * scopeHtmlWidgetIds) are pure functions with no React dependency — usable
 * server-side or in tests without a DOM (where guarded).
 *
 * fetchFragment and reinitFragmentScripts, at the bottom of this file, are the
 * exception: they dispatch Redux actions and drive DOM script injection on
 * top of the pure helpers above. They live here (not in useFragmentLoader.js,
 * their sole caller) because they need no React state of their own — just the
 * pure helpers this module already provides — and colocating them avoids a
 * hook file that's mostly non-hook orchestration logic.
 *
 * Preferred entry point for the full parsing pipeline:
 *   const { scripts, styles, states, html } = parseFragment(rawHtml);
 *
 * Individual exports (sanitizeFragmentHtml, extractFragment*) remain available
 * for independent use and unit testing.
 */
import logger from 'ws-scripts/modules/logger';
import {
	fragmentFetchStarted,
	fragmentFetchSucceeded,
	fragmentFetchFailed
} from '../features/fragmentsSlice.js';
import fireModulesRefresh from './fireModulesRefresh.js';

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Returns true if a URL is safe to inject — either:
 *   - relative (no http/https scheme), or
 *   - an absolute URL whose origin matches the current page origin.
 *
 * Used by the *FromDoc extractors to enforce a same-origin policy before
 * any external asset is injected.
 *
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
const isSameOriginOrRelative = (url) => {
	if (!url) return false;
	if (!/^https?:\/\//i.test(url)) return true;
	try {
		return new URL(url).origin === window.location.origin;
	} catch {
		return false;
	}
};

/**
 * Non-prod page hosts (local dev + deployed non-prod). Prod pages are served
 * from `*.web.dealer.com`, which never matches — so on prod the script filter
 * stays strictly same-origin.
 */
const NONPROD_PAGE = /\.(?:localhost|web-np)\.dealer\.com$/i;

/**
 * The ws-scripts HMR dev-server bundle host. `hot-module-replacement.js`
 * always builds this as `${configuredPort()}.localport.dealer.com` (e.g.
 * `8081.localport.dealer.com`) — a numeric port label, never an arbitrary
 * subdomain — so the pattern is anchored to digits-only to match that real
 * convention instead of allowing any `*.localport.dealer.com` label.
 */
const DEV_BUNDLE = /^\d+\.localport\.dealer\.com$/i;

const isDevBundleHost = (src) => {
	try {
		return DEV_BUNDLE.test(new URL(src).host);
	} catch {
		return false;
	}
};

/**
 * Whether a script src is permitted for injection. Same-origin/relative always
 * passes; additionally, on a non-prod page the cross-origin ws-scripts dev
 * bundle is allowed so the widget can hydrate locally. Prod pages and unknown
 * hosts fail closed (strict same-origin only).
 *
 * `pageHostname` is injected (defaulting to the current page host) so the
 * security-critical policy can be unit-tested without stubbing window.location.
 *
 * @param {string|null|undefined} src
 * @param {string} [pageHostname] current page hostname
 * @returns {boolean}
 */
export const isAllowedScriptSrc = (
	src,
	pageHostname = window.location.hostname
) =>
	isSameOriginOrRelative(src) ||
	(NONPROD_PAGE.test(pageHostname) && isDevBundleHost(src));

/**
 * Idempotently injects an asset element into document.head.
 * Resolves immediately if an element matching (selector + attr === value) already
 * exists in the DOM. Rejects if the injected element fires an error event.
 *
 * With `force`, an existing matching element is removed and a fresh one
 * inserted instead of short-circuiting. This is for scripts whose bundle
 * only runs its init logic when the <script> element itself is inserted
 * (not via a persistent event listener). It's needed when a hook instance
 * mounts with the fragment ALREADY cached by another embed of the same url
 * (e.g. the mega menu fetched the model-selector, then the drawer mounts its
 * own copy): the script src is already in <head> from the first embed, so a
 * plain injectScript would short-circuit and the bundle would never run
 * against this instance's freshly-rendered dangerouslySetInnerHTML tree.
 * force re-inserts it so the init logic runs again.
 *
 * @param {string} selector - CSS selector to detect existing elements
 * @param {string} attr - attribute name to compare ('src' or 'href')
 * @param {string} value - attribute value
 * @param {() => HTMLElement} buildElement - creates and configures the element
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<void>}
 */
const injectAsset = (
	selector,
	attr,
	value,
	buildElement,
	{ force = false } = {}
) =>
	new Promise((resolve, reject) => {
		const existing = Array.from(document.querySelectorAll(selector)).filter(
			(el) => el.getAttribute(attr) === value
		);
		if (existing.length > 0) {
			// Already on the page. Without force this is a no-op — correct for a
			// stylesheet, but wrong for a bundle whose bootstrap must run again to
			// hydrate a newly-injected instance (see injectScript force use in
			// useFragmentLoader). With force, remove the stale element(s) and
			// re-insert a fresh one so its init logic re-runs.
			if (!force) {
				resolve();
				return;
			}
			existing.forEach((el) => el.remove());
		}
		const el = buildElement();
		el.onload = resolve;
		el.onerror = reject;
		document.head.appendChild(el);
	});

/**
 * Regex for DDC.WS.state assignment patterns.
 * Defined once at module scope; safe to share across matchAll() calls because
 * matchAll() does not mutate the regex's lastIndex on the original object.
 *
 * Captures: [1] widgetName, [2] widgetId
 */
const ASSIGNMENT_RE = /DDC\.WS\.state\['([^']+)'\]\['([^']+)'\]\s*=\s*/g;

/**
 * Property names that must never be used as object keys on window.DDC.WS.state.
 * Using __proto__, constructor, or prototype as a key would allow an attacker to
 * pollute Object.prototype or Function.prototype via the state assignment.
 */
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Finds the end of the JSON object starting at `openBraceIndex` by scanning
 * forward with a brace-depth counter that is string- and escape-aware (braces
 * inside string values, and `\"`, don't count). Returns the exclusive end index
 * (one past the matching '}'), or -1 if the braces never balance — matching the
 * String.prototype.indexOf convention. The caller slices
 * content[openBraceIndex..end] and JSON.parses just that.
 *
 * Why the manual scan: ws-scripts widgets serialize their preloaded Redux state
 * as `DDC.WS.state['name']['id'] = { ... };` right before their bundle.js, and
 * our sanitizer strips every <script>, so we must pull that JSON out first. It
 * lives inside a JS statement (can't JSON.parse the whole script), a greedy
 * /{.*}/ breaks on braces inside string values, and eval is banned by policy.
 *
 * @param {string} content - the full text to scan
 * @param {number} openBraceIndex - index of the opening '{' character
 * @returns {number} index one past the matching '}', or -1 if unmatched
 */
const findJsonObjectEnd = (content, openBraceIndex) => {
	let braceDepth = 0;
	let insideString = false;
	let nextIsEscaped = false;

	for (let i = openBraceIndex; i < content.length; i++) {
		const char = content[i];

		if (nextIsEscaped) {
			// Previous character was a backslash; treat this character as literal.
			nextIsEscaped = false;
		} else if (char === '\\' && insideString) {
			// Backslash inside a string — the next character is escaped.
			nextIsEscaped = true;
		} else if (char === '"') {
			// Toggle: entering or exiting a JSON string literal.
			insideString = !insideString;
		} else if (!insideString) {
			// Outside a string: track brace nesting to find the matching '}'.
			if (char === '{') {
				braceDepth += 1;
			} else if (char === '}') {
				braceDepth -= 1;
				if (braceDepth === 0) {
					return i + 1; // exclusive end: one past the closing '}'
				}
			}
		}
	}

	return -1; // unterminated object — braces never balanced
};

/**
 * Extracts a single DDC.WS.state assignment from a match found in a script's
 * content string. Returns null when the widgetName or widgetId is a prototype
 * pollution key, the value is not a valid JSON object, or is malformed.
 *
 * @param {string} content - full text content of an inline <script> element
 * @param {RegExpMatchArray} m - a match from content.matchAll(ASSIGNMENT_RE)
 * @returns {WidgetStateEntry|null}
 */
const extractStateEntry = (content, m) => {
	const widgetName = m[1];
	const widgetId = m[2];

	// Prototype pollution guard — reject dangerous property names that could be
	// used to pollute Object.prototype or Function.prototype via state assignment.
	if (DANGEROUS_KEYS.has(widgetName) || DANGEROUS_KEYS.has(widgetId)) {
		return null;
	}

	const jsonStart = m.index + m[0].length;

	// Must start with an object literal
	if (content[jsonStart] !== '{') return null;

	const jsonEnd = findJsonObjectEnd(content, jsonStart);
	if (jsonEnd === -1) return null;

	try {
		return {
			widgetName,
			widgetId,
			state: JSON.parse(content.slice(jsonStart, jsonEnd))
		};
	} catch {
		// Malformed JSON — skip silently
		return null;
	}
};

// ─── Document-based helpers (accept an already-parsed Document) ───────────────
//
// These carry the real implementation logic. The string-based public exports
// are thin wrappers that parse and delegate here. parseFragment() composes
// all four in a single DOMParser.parseFromString call.

/**
 * Sanitizes an already-parsed Document in-place and returns the body innerHTML.
 * Removes script/style/link elements, strips inline event handlers (on*),
 * javascript: URIs, and non-image data: URIs on any attribute.
 * Returns null when a .page-header element is found (full-page response validity gate).
 *
 * @param {Document} doc
 * @returns {string|null}
 */
const sanitizeDoc = (doc) => {
	// Validity gate — full-page response
	if (doc.querySelector('.page-header')) {
		return null;
	}

	// Remove script, style, and external stylesheet elements
	doc.querySelectorAll('script, style, link[rel="stylesheet"]').forEach(
		(el) => el.remove()
	);

	// Strip inline event handler attributes (on*) and javascript: URI attributes
	/* eslint-disable no-script-url */
	doc.querySelectorAll('*').forEach((el) => {
		Array.from(el.attributes).forEach((attr) => {
			if (attr.name.startsWith('on')) {
				el.removeAttribute(attr.name);
			} else if (
				/^javascript:/i.test(attr.value.trim()) ||
				/^data:(?!image\/)/i.test(attr.value.trim())
			) {
				el.removeAttribute(attr.name);
			}
		});
	});
	/* eslint-enable no-script-url */

	return doc.body.innerHTML;
};

/**
 * Extracts permitted script src values from an already-parsed Document:
 * same-origin/relative always, plus the cross-origin ws-scripts dev bundle on
 * non-prod pages (see isAllowedScriptSrc). Must be called before sanitizeDoc
 * removes <script> elements.
 *
 * @param {Document} doc
 * @returns {string[]}
 */
const extractScriptsFromDoc = (doc) =>
	Array.from(doc.querySelectorAll('script[src]'))
		.map((el) => el.getAttribute('src'))
		.filter((src) => isAllowedScriptSrc(src));

/**
 * Extracts deduplicated same-origin stylesheet hrefs from an already-parsed
 * Document, covering both <link rel="stylesheet"> and <style data-href>.
 * Must be called before sanitizeDoc removes those elements.
 *
 * @param {Document} doc
 * @returns {string[]}
 */
const extractStylesFromDoc = (doc) => {
	const linkHrefs = Array.from(
		doc.querySelectorAll('link[rel="stylesheet"][href]')
	).map((el) => el.getAttribute('href'));

	const inlineStyleHrefs = Array.from(
		doc.querySelectorAll('style[data-href]')
	).map((el) => el.getAttribute('data-href'));

	return [...new Set([...linkHrefs, ...inlineStyleHrefs])].filter(
		isSameOriginOrRelative
	);
};

/**
 * Extracts DDC.WS.state entries from inline scripts in an already-parsed
 * Document. Must be called before sanitizeDoc removes <script> elements.
 *
 * @param {Document} doc
 * @returns {WidgetStateEntry[]}
 */
const extractStatesFromDoc = (doc) => {
	const inlineScripts = Array.from(doc.querySelectorAll('script:not([src])'));
	return inlineScripts.flatMap((script) => {
		const content = script.textContent || '';
		return Array.from(content.matchAll(ASSIGNMENT_RE))
			.map((m) => extractStateEntry(content, m))
			.filter(Boolean);
	});
};

// ─── Exported utilities ────────────────────────────────────────────────────────

/**
 * @typedef {Object} ParsedFragment
 * @property {string[]} scripts - same-origin script src values to inject
 * @property {string[]} styles - deduplicated same-origin stylesheet hrefs to inject
 * @property {WidgetStateEntry[]} states
 *   DDC.WS.state entries extracted from inline scripts
 * @property {string|null} html - sanitized innerHTML ready for injection,
 *   or null when the response is a full page (validity gate) or otherwise invalid
 */

/**
 * Parses a fragment HTML response ONCE and returns all data needed for hydration.
 *
 * This is the preferred entry point when processing a fragment for injection.
 * Calling the individual extract* / sanitize* functions separately on the same
 * rawHtml string would parse the DOM four times; this facade parses it once.
 *
 * Extraction runs before sanitization because sanitizeDoc removes the <script>
 * and <style> elements that the extractors need.
 *
 * @param {string} rawHtml - unsanitized HTML string from the fragment endpoint
 * @returns {ParsedFragment}
 */
export const parseFragment = (rawHtml) => {
	// SSR guard
	if (typeof window === 'undefined') {
		return { scripts: [], styles: [], states: [], html: null };
	}

	const doc = new DOMParser().parseFromString(rawHtml, 'text/html');

	// Extract BEFORE sanitizing — sanitizeDoc removes <script> and <style>.
	const scripts = extractScriptsFromDoc(doc);
	const styles = extractStylesFromDoc(doc);
	const states = extractStatesFromDoc(doc);

	// sanitizeDoc mutates the doc (removes elements); call last.
	const html = sanitizeDoc(doc);

	return { scripts, styles, states, html };
};

/**
 * Sanitizes raw fragment HTML before injection.
 * Strips <script>, <style>, <link rel="stylesheet">, inline event handlers,
 * javascript: URIs, non-image data: URIs, and validates the response is not
 * a full page (presence of .page-header signals a full-page response).
 *
 * Prefer parseFragment() when you also need scripts/styles/states — it
 * avoids a redundant DOMParser call.
 *
 * @param {string} html - raw HTML string from the fragment endpoint
 * @returns {string|null} sanitized innerHTML string, or null if invalid
 */
export const sanitizeFragmentHtml = (html) =>
	sanitizeDoc(new DOMParser().parseFromString(html, 'text/html'));

/**
 * Extracts same-origin script src values from raw fragment HTML.
 * Must be called on the raw HTML string BEFORE sanitizeFragmentHtml
 * strips all <script> elements.
 *
 * Relative paths are always allowed. Absolute http(s) URLs are allowed
 * only when their origin matches window.location.origin. Cross-origin
 * URLs and inline scripts (no src attribute) are silently dropped.
 *
 * Prefer parseFragment() when you also need styles/states/sanitized HTML.
 *
 * @param {string} html - raw HTML string from the fragment endpoint
 * @returns {string[]} array of allowed script src values
 */
export const extractFragmentScripts = (html) => {
	// SSR guard
	if (typeof window === 'undefined') return [];
	return extractScriptsFromDoc(
		new DOMParser().parseFromString(html, 'text/html')
	);
};

/**
 * Extracts same-origin stylesheet hrefs from raw fragment HTML.
 * Handles two patterns:
 *   1. <link rel="stylesheet" href="..."> — standard external stylesheet
 *   2. <style data-href="..."> — ws-scripts inline CSS (ws-scripts-inline-css flag).
 *      The CSS content is inlined into the HTML but data-href retains the source URL
 *      so we can inject it as an external stylesheet instead of losing it.
 *
 * Must be called before sanitizeFragmentHtml, which strips both element types.
 *
 * Relative paths (no http/https scheme) are always allowed.
 * Absolute URLs are allowed only if they share the page's origin.
 * Invalid or empty hrefs are silently dropped. Duplicate hrefs are deduplicated.
 *
 * Prefer parseFragment() when you also need scripts/states/sanitized HTML.
 *
 * @param {string} rawHtml - unsanitized HTML string from fragment fetch response
 * @returns {string[]} deduplicated array of allowed stylesheet hrefs
 */
export const extractFragmentStyles = (rawHtml) => {
	// SSR guard
	if (typeof window === 'undefined') return [];
	return extractStylesFromDoc(
		new DOMParser().parseFromString(rawHtml, 'text/html')
	);
};

/**
 * Extracts DDC.WS.state widget-state assignments from inline scripts in a
 * fragment HTML response, WITHOUT using eval.
 *
 * ws-scripts widgets serialize their Redux preloaded state as an inline script
 * immediately before their bundle.js:
 *
 *   DDC.WS.state['ws-model-selector']['navigation-model-selector1'] = { ... };
 *
 * Our sanitizer strips all <script> elements (including this block), so without
 * this function the injected bundle.js finds no state and cannot hydrate.
 *
 * Algorithm:
 *   1. Parse all inline <script> (no src) elements from the raw HTML.
 *   2. For each, find assignment patterns using matchAll (no lastIndex management).
 *   3. Delegate per-match extraction to extractStateEntry, which uses a
 *      brace-depth counter to locate the JSON object boundary.
 *   4. JSON.parse the extracted slice — no eval, no Function constructor.
 *
 * Malformed JSON or unrecognised patterns are silently skipped.
 *
 * Prefer parseFragment() when you also need scripts/styles/sanitized HTML.
 *
 * @param {string} rawHtml - unsanitized HTML string from the fragment fetch response
 * @returns {WidgetStateEntry[]}
 */
export const extractFragmentState = (rawHtml) => {
	// SSR guard
	if (typeof window === 'undefined') return [];
	return extractStatesFromDoc(
		new DOMParser().parseFromString(rawHtml, 'text/html')
	);
};

/**
 * Injects a script element into document.head.
 * Without `force`: no-op and resolves immediately if a script with the same src
 * already exists. With `force`: removes any existing matching script and injects
 * a fresh one so its init logic re-runs (see injectAsset for why re-init needs
 * that). Resolves when the script loads, rejects on error.
 *
 * @param {string} src - script URL to inject
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<void>}
 */
export const injectScript = (src, options) =>
	injectAsset(
		'script[src]',
		'src',
		src,
		() => {
			const el = document.createElement('script');
			el.async = true;
			el.src = src;
			return el;
		},
		options
	);

/**
 * Injects a stylesheet link element into document.head.
 * No-op and resolves immediately if a stylesheet with the same href already exists.
 * Resolves when the stylesheet loads, rejects on error.
 *
 * @param {string} href - stylesheet URL to inject
 * @returns {Promise<void>}
 */
export const injectStylesheet = (href) =>
	injectAsset('link[rel="stylesheet"]', 'href', href, () => {
		const el = document.createElement('link');
		el.rel = 'stylesheet';
		el.href = href;
		return el;
	});

/**
 * Applies extracted DDC.WS.state entries to window.DDC.WS.state.
 *
 * ws-scripts widgets expect their Redux preloaded state to be present on
 * window.DDC.WS.state when their bundle.js executes. Call this function
 * BEFORE injecting any script assets so the state is available when the
 * bundle runs.
 *
 * Only entries whose slot in window.DDC.WS.state is currently unset are
 * written — existing state is never overwritten (e.g. if the widget's own
 * bundle already ran on a shared page).
 *
 * @param {WidgetStateEntry[]} fragmentStates
 *   Entries from extractFragmentState() or ParsedFragment.states.
 * @returns {void}
 */
export const applyFragmentState = (fragmentStates) => {
	// SSR guard
	if (typeof window === 'undefined') return;
	if (!fragmentStates.length) return;

	window.DDC = window.DDC || {};
	window.DDC.WS = window.DDC.WS || {};
	window.DDC.WS.state = window.DDC.WS.state || {};

	fragmentStates.forEach(({ widgetName, widgetId, state }) => {
		window.DDC.WS.state[widgetName] = window.DDC.WS.state[widgetName] || {};
		// Guard: don't overwrite state that has already been initialized
		// (e.g. if the widget's own bundle ran first on a shared page).
		if (!window.DDC.WS.state[widgetName][widgetId]) {
			window.DDC.WS.state[widgetName][widgetId] = state;
		}
	});
};

/**
 * Rewrites a fragment's `{widgetId}-app-root` container id(s) to scope them to
 * one mount instance.
 *
 * The exact same fragment URL/content can be embedded in more than one place
 * on the page at once (e.g. the mega menu and the drawer both referencing the
 * same nav item's embedded widget). Each embed injects the identical cached
 * html, so without this rewrite two elements on the page would share the
 * literal id the widget's own bootstrap looks up via a plain
 * document.getElementById — which always resolves to whichever one is first
 * in document order, regardless of which instance's script actually
 * triggered it. Scoping the id per instance (see scopeStates, which must be
 * given the same suffix) makes each mount independently addressable.
 *
 * @param {string|null} html - sanitized fragment HTML, or null
 * @param {WidgetStateEntry[]} states
 *   - pristine (unscoped) state entries; identifies which container ids exist
 * @param {string} suffix - identifier stable for one mount instance's lifetime
 * @returns {string|null}
 */
export const scopeHtmlWidgetIds = (html, states, suffix) => {
	// SSR guard
	if (typeof window === 'undefined') return html;
	if (!html || !states || states.length === 0) return html;

	const doc = new DOMParser().parseFromString(html, 'text/html');
	states.forEach(({ widgetId }) => {
		const el = doc.getElementById(`${widgetId}-app-root`);
		if (el) {
			el.id = `${widgetId}--${suffix}-app-root`;
		}
	});
	return doc.body.innerHTML;
};

/**
 * Scopes each state entry's widgetId to one mount instance, matching the
 * container id rewrite scopeHtmlWidgetIds applies to the same fragment's
 * html. Pass the result to applyFragmentState so window.DDC.WS.state is
 * written under the same key the widget's bootstrap will look up via
 * document.getElementById(`${widgetId}-app-root`).
 *
 * @param {WidgetStateEntry[]} states
 * @param {string} suffix - identifier stable for one mount instance's lifetime
 * @returns {WidgetStateEntry[]}
 */
export const scopeStates = (states, suffix) =>
	(states || []).map((entry) => ({
		...entry,
		widgetId: `${entry.widgetId}--${suffix}`
	}));

// ─── Fragment loading orchestration (Redux + DOM side effects) ────────────────
//
// Unlike everything above, these two functions are not pure: they dispatch
// Redux actions and inject/re-run scripts against the live DOM. Both are
// called fire-and-forget by useFragmentLoader (never awaited) — their own
// try/catch blocks are what keep an asset failure from surfacing as an
// unhandled rejection there.

/**
 * Fetches fragment HTML for the given URL, sanitizes it, dispatches Redux actions,
 * and injects any same-origin scripts and stylesheets.
 * Calls fireModulesRefresh after all assets have loaded (or immediately if none).
 *
 * @param {string} url
 * @param {Function} dispatch - Redux dispatch
 * @param {string} suffix - this hook instance's stable id-scoping suffix
 *   (see scopeStates/scopeHtmlWidgetIds above)
 * @returns {Promise<void>}
 */
export const fetchFragment = async (url, dispatch, suffix) => {
	try {
		dispatch(fragmentFetchStarted(url));

		const response = await fetch(url, { headers: { Accept: 'text/html' } });

		if (!response.ok) {
			logger.warn(
				`[useFragmentLoader] non-ok response for ${url}: ${response.status}`
			);
			dispatch(fragmentFetchFailed(url));
			return;
		}

		const rawHtml = await response.text();
		const { scripts, styles, states, html } = parseFragment(rawHtml);

		if (html === null) {
			logger.warn(
				`[useFragmentLoader] full-page or invalid response for ${url}`
			);
			dispatch(fragmentFetchFailed(url));
			return;
		}

		// Apply widget state to window.DDC.WS.state BEFORE injecting bundle.js.
		// ws-scripts widgets expect this to be present when their bundle executes.
		// parseFragment extracts state from the fragment's inline <script> blocks
		// so sanitizeDoc can safely remove all <script> tags. Scoped to this
		// instance's suffix so it lines up with the container id this instance's
		// copy of the html will render with (see scopeHtmlWidgetIds).
		applyFragmentState(scopeStates(states, suffix));

		// scripts and states are cached alongside html, UNscoped, so every
		// mount instance sharing this url can derive its own scoped copy (see
		// reinitFragmentScripts, called from useFragmentLoader's renderKey
		// effect) without re-fetching the fragment.
		dispatch(fragmentFetchSucceeded({ url, html, scripts, states }));

		const assets = [
			// force: true — the host page may already have this widget's bundle
			// <script> (e.g. its own ws-rec-vehicles), whose bootstrap ran at page
			// load before this instance's scoped state key existed. Without force,
			// injectScript would skip the existing <script> and our lazily-injected
			// instance would never hydrate → empty shell. force re-inserts it so the
			// bootstrap re-iterates DDC.WS.state and picks up our scoped key.
			// Safe because ws-scripts bundles are idempotent: the entry point reads
			// DDC.WS.state[name][id] once and deletes it after hydrating, so re-runs
			// only hydrate the new key. A non-ws-scripts bundle with unguarded
			// bootstrap side effects would not be a safe fit here.
			...scripts.map((src) => injectScript(src, { force: true })),
			...styles.map(injectStylesheet)
		];

		// Pass null (not a scoped subset) to fireModulesRefresh: a fragment's
		// html can contain markup for any DDC module (map, carousel, etc.), not
		// just lazyImage, and those modules' init logic isn't delivered
		// per-fragment (parseFragment only extracts <script src> elements — a
		// fragment's inline widget-data scripts are stripped by sanitizeDoc).
		// The modules themselves are already loaded on the host page, waiting
		// for a modulesRefresh event to notice their selector now exists in the
		// DOM. Scoping this to a subset would silently skip initializing
		// everything else the fragment just rendered.
		if (assets.length === 0) {
			fireModulesRefresh(null);
			return;
		}

		try {
			await Promise.all(assets);
			fireModulesRefresh(null);
		} catch (err) {
			logger.warn('[useFragmentLoader] asset inject error:', err);
		}
	} catch (err) {
		logger.warn(`[useFragmentLoader] fetch error for ${url}:`, err);
		dispatch(fragmentFetchFailed(url));
	}
};

/**
 * Re-runs a fragment's already-fetched script(s) against its current DOM.
 *
 * A fragment's bundle.js runs its init logic at the moment its <script>
 * element is inserted — it is not a persistent listener that reacts to a
 * generic "refresh" broadcast. injectScript's normal dedup logic is exactly
 * wrong here: it would see the script is already present and skip
 * re-running it. { force: true } removes the stale element and inserts a
 * fresh one so the bundle executes again, against whatever DOM currently
 * exists (either a freshly remounted dangerouslySetInnerHTML tree, or the
 * same still-mounted tree that just became visible again).
 *
 * ws-scripts' generated browser entry point (see any widget's
 * src/ws-scripts/browser.jsx) reads window.DDC.WS.state[widgetName][widgetId]
 * exactly once and deletes that entry immediately after hydrating, by design,
 * so it can't accidentally double-hydrate. That means the very state this
 * function's script reinjection depends on is already gone by the time this
 * runs a second time, unless it's written back first — applyFragmentState
 * must run again here, exactly as it does before the first injection in
 * fetchFragment above, or the widget's own init finds no widget IDs to
 * hydrate and silently does nothing (no React tree, no listeners, no
 * re-render — not an error, just a no-op).
 *
 * @param {string[]} scripts - cached same-origin script src values
 * @param {WidgetStateEntry[]} states - cached (unscoped) DDC.WS.state entries
 *   to restore before scripts re-run
 * @param {string} suffix - this hook instance's stable id-scoping suffix
 * @returns {Promise<void>}
 */
export const reinitFragmentScripts = async (scripts, states = [], suffix) => {
	if (typeof window === 'undefined') return;

	applyFragmentState(scopeStates(states, suffix));

	// Unscoped — see the matching comment in fetchFragment above.
	if (!scripts || scripts.length === 0) {
		fireModulesRefresh(null);
		return;
	}

	try {
		await Promise.all(
			scripts.map((src) => injectScript(src, { force: true }))
		);
		fireModulesRefresh(null);
	} catch (err) {
		logger.warn('[useFragmentLoader] reinit script error:', err);
	}
};
